// A customer's page (boards CustStatement, CustStatementPhone, CustPayments; plan in
// docs/plans/customer-statement.md): where they stand in four numbers, then their statement,
// bills, payments and quotes. The statement and what is owed are worked out on this device
// from `parties.customerLedger`, never stored.
import { type BuildContext } from '@ims/core'
import type { CustomerLedger, CustomerRow } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Segmented,
  Select,
  SkeletonDetail,
  SkeletonTable,
  Stat,
  TD,
  TH,
  TR,
  Table,
  buttonClass,
  cx,
  useToast,
} from '@ims/ui'
import { ArrowLeft, FilePdf, HandCoins, Pencil, Phone } from '@ims/ui/icons'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { DateInput } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { settlePaidAhead } from '../lib/allocate.ts'
import { dateLabel, dateShort, money, todayIso } from '../lib/format.ts'
import { calendarOf, daysBetween, rangePresets } from '../lib/reports.ts'
import {
  buildStatement,
  customerStanding,
  methodLabel,
  rowWhat,
  type StatementKind,
  type StatementRow,
} from '../lib/statement.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { CANCELLED_ROW, StatusBadge } from './invoices.tsx'
import { CustomerPaymentDialog, PartyForm } from './parties.tsx'
import { CustomerSalesOrders } from './sales-orders.tsx'
import { StatementSheet, type StatementPeriod } from './statement-sheet.tsx'

type View = 'statement' | 'bills' | 'payments' | 'orders'
/** The tables a customer's ledger is read from (`parties.customerLedger`). */
export const LEDGER_TABLES = [
  'sales_invoices',
  'payments',
  'payment_allocations',
  'credit_notes',
] as const
const PERIODS: StatementPeriod[] = ['month', 'last-month', 'year', 'last-year', 'custom']

/** The dot before a statement line: a bill, a payment, a credit note. */
const DOT: Record<StatementKind, string> = {
  bill: 'bg-neutral',
  payment: 'bg-success',
  credit: 'bg-insight',
}

export function CustomerDetailScreen() {
  const { id = '' } = useParams()
  const settings = useSettings()
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const [params, setParams] = useSearchParams()
  const canEdit = useCan('waiter')
  const canPay = useCan('waiter')
  // What is owed, the statement and payments: staff and up, as on the Customers list.
  const canMoney = useCan('staff') && Boolean(repo.parties)
  const [editing, setEditing] = useState(false)
  const [paying, setPaying] = useState(false)
  const [sheet, setSheet] = useState<StatementPeriod | null>(null)
  const today = todayIso()

  const customer = useLive((r) => r.get('customers', id), [id], ['customers'])
  const ledger = useLive(
    async (r): Promise<CustomerLedger | null> => (r.parties ? r.parties.customerLedger(id) : null),
    [id],
    [...LEDGER_TABLES],
  ).data

  const view = ((): View => {
    const asked = params.get('view') as View | null
    if (asked === 'bills' || asked === 'orders') return asked
    if ((asked === 'statement' || asked === 'payments') && canMoney) return asked
    return canMoney ? 'statement' : 'bills'
  })()
  const set = (changes: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        for (const [k, v] of Object.entries(changes)) {
          if (v === null) next.delete(k)
          else next.set(k, v)
        }
        return next
      },
      { replace: true },
    )

  const presets = rangePresets(today, settings)
  const periodParam = params.get('period') as StatementPeriod | null
  const period: StatementPeriod =
    periodParam && PERIODS.includes(periodParam) ? periodParam : 'year'
  const preset = presets.find((p) => p.key === period)
  const range =
    period === 'custom'
      ? { from: params.get('from') ?? preset?.from ?? today, to: params.get('to') ?? today }
      : { from: preset!.from, to: preset!.to }

  if (!customer.data)
    return customer.loading ? <SkeletonDetail /> : <EmptyState title="Customer not found" />
  const c: CustomerRow = customer.data
  const standing = ledger ? customerStanding(ledger, today) : null
  const openTotal = standing?.openBills.reduce((sum, b) => sum + b.openPaisa, 0) ?? 0
  const ctx = (): BuildContext => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })
  const settleAhead = async () => {
    try {
      const set = await settlePaidAhead(repo, ctx(), c.id)
      if (set > 0) toast.success(`${money(set, settings)} set against their bills`)
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const contact = [c.phone, c.pan_vat_number && `PAN ${c.pan_vat_number}`, c.address]
    .filter(Boolean)
    .join(' · ')

  return (
    <div>
      <Link
        to="/customers"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Customers
      </Link>
      {/* PageHeader keeps its buttons on one line; here four buttons must wrap on a phone, two
          a row, Take payment and Statement PDF first (board CustStatementPhone). */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-x-3 gap-y-3">
        <div className="min-w-0 flex-1 basis-72">
          <h1 className="text-page-title break-words text-ink">{c.name}</h1>
          {contact && <p className="mt-1 text-sm text-muted">{contact}</p>}
        </div>
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
          {c.phone && (
            <a
              href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}
              aria-label={`Call ${c.name}`}
              className={buttonClass({ variant: 'secondary', className: 'order-3 sm:hidden' })}
            >
              <Phone className="size-4" /> Call
            </a>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              className="order-4 sm:order-none"
              icon={<Pencil className="size-4" />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
          {canMoney && ledger && (
            <Button
              variant="secondary"
              className="order-2 sm:order-none"
              icon={<FilePdf className="size-4" />}
              onClick={() => setSheet(period)}
            >
              Statement PDF
            </Button>
          )}
          {canPay && ledger && (
            <Button
              variant="soft"
              className="order-1 sm:order-none"
              icon={<HandCoins className="size-4" />}
              onClick={() => setPaying(true)}
            >
              Take payment
            </Button>
          )}
        </div>
      </div>

      {canMoney && (
        <div
          className={cx(
            'mb-4 grid grid-cols-2 gap-3 sm:gap-4',
            c.credit_limit_paisa ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
          )}
        >
          <Stat
            label="Owed now"
            value={standing ? money(standing.owedPaisa, settings) : undefined}
            hint={
              standing && standing.paidAheadPaisa > 0
                ? `After ${money(standing.paidAheadPaisa, settings)} paid ahead`
                : standing && standing.owedPaisa < 0
                  ? 'They have money with you'
                  : undefined
            }
          />
          <Stat
            label="Late"
            className={standing && standing.latePaisa > 0 ? '[&_.tabular]:text-danger' : undefined}
            value={standing ? money(standing.latePaisa, settings) : undefined}
            hint={
              standing && standing.lateBills > 0 ? (
                <Link
                  to="?view=bills&late=1"
                  replace
                  className="underline decoration-dotted underline-offset-2 hover:text-ink"
                >
                  {standing.lateBills} bill{standing.lateBills === 1 ? '' : 's'},{' '}
                  {standing.mostLateDays} day{standing.mostLateDays === 1 ? '' : 's'} past due
                </Link>
              ) : (
                'Nothing past its due date'
              )
            }
          />
          {c.credit_limit_paisa ? (
            <Stat
              label="Credit limit"
              value={money(c.credit_limit_paisa, settings)}
              hint={
                standing && <CreditBar owed={standing.owedPaisa} limit={c.credit_limit_paisa} />
              }
            />
          ) : null}
          <Stat
            label="Oldest unpaid"
            value={
              standing
                ? standing.oldestOpen
                  ? daysWords(daysBetween(standing.oldestOpen.date, today))
                  : 'None'
                : undefined
            }
            hint={
              standing?.oldestOpen &&
              `${standing.oldestOpen.number ?? 'Bill'} from ${dateShort(standing.oldestOpen.date, settings)}`
            }
          />
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <Segmented<View>
          label="Show"
          value={view}
          onChange={(v) => set({ view: v === 'statement' ? null : v, late: null, p: null })}
          className="max-sm:w-full"
          options={[
            ...(canMoney ? [{ value: 'statement' as const, label: 'Statement' }] : []),
            { value: 'bills' as const, label: 'Bills' },
            ...(canMoney ? [{ value: 'payments' as const, label: 'Payments' }] : []),
            ...(!settings?.restaurant
              ? [
                  {
                    value: 'orders' as const,
                    label: (
                      <>
                        <span className="max-sm:hidden">Quotes and orders</span>
                        <span className="sm:hidden">Orders</span>
                      </>
                    ),
                  },
                ]
              : []),
          ]}
        />
        {(view === 'statement' || view === 'payments') && (
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full sm:ml-auto">
            <div className="w-full sm:w-60">
              <Select
                aria-label="Period"
                value={period}
                onChange={(e) =>
                  set({
                    period: e.target.value === 'year' ? null : e.target.value,
                    from: e.target.value === 'custom' ? range.from : null,
                    to: e.target.value === 'custom' ? range.to : null,
                  })
                }
              >
                {presets
                  .filter((p) => PERIODS.includes(p.key as StatementPeriod))
                  .map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                <option value="custom">Choose dates…</option>
              </Select>
            </div>
            <p
              className="text-sm text-muted"
              title={`${dateLabel(range.from, settings)} to ${dateLabel(range.to, settings)}`}
            >
              {dateShort(range.from, settings)} to {dateShort(range.to, settings)}
            </p>
          </div>
        )}
      </div>
      {period === 'custom' && (view === 'statement' || view === 'payments') && (
        <Card className="mb-3 flex flex-wrap items-end gap-3 p-4">
          {(['from', 'to'] as const).map((end) => (
            <DateInput
              key={end}
              label={end === 'from' ? 'From' : 'To'}
              calendar={calendarOf(settings)}
              className={calendarOf(settings) === 'bs' ? 'w-full sm:w-[21rem]' : 'w-full sm:w-44'}
              value={range[end]}
              onChange={(iso) => set({ [end]: iso })}
            />
          ))}
        </Card>
      )}

      {view === 'statement' &&
        (ledger ? (
          <StatementCard ledger={ledger} range={range} />
        ) : (
          <Card className="p-0">
            <SkeletonTable rows={5} />
          </Card>
        ))}
      {view === 'payments' &&
        (ledger ? (
          <PaymentsCard
            ledger={ledger}
            range={range}
            selected={params.get('p')}
            canUse={canPay && openTotal > 0}
            onUseAhead={() => void settleAhead()}
          />
        ) : (
          <Card className="p-0">
            <SkeletonTable rows={4} />
          </Card>
        ))}
      {view === 'bills' && <BillsCard customerId={id} lateOnly={params.get('late') === '1'} />}
      {view === 'orders' && (
        <div className="grid gap-4 empty:hidden lg:grid-cols-2">
          <CustomerSalesOrders customerId={id} kind="quotation" />
          <CustomerSalesOrders customerId={id} kind="order" />
        </div>
      )}

      {editing && <PartyForm kind="customers" party={c} onClose={() => setEditing(false)} />}
      {paying && (
        <CustomerPaymentDialog
          customer={c}
          owedPaisa={openTotal}
          ahead
          onClose={() => setPaying(false)}
          onShare={canMoney ? () => setSheet('month') : undefined}
        />
      )}
      {sheet && ledger && (
        <StatementSheet
          customer={c}
          ledger={ledger}
          today={today}
          period={sheet}
          range={range}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  )
}

function daysWords(days: number): string {
  return days <= 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`
}

function CreditBar({ owed, limit }: { owed: number; limit: number }) {
  const settings = useSettings()
  const used = Math.max(0, Math.min(100, Math.round((owed / limit) * 100)))
  const over = owed > limit
  return (
    <span className="block">
      <span
        role="img"
        aria-label={`${used}% of the credit limit used`}
        className="mt-1 mb-1.5 block h-1.5 overflow-hidden rounded-full bg-soft"
      >
        <span
          className={cx('block h-full rounded-full', over ? 'bg-danger' : 'bg-warning')}
          style={{ width: `${used}%` }}
        />
      </span>
      {over
        ? `${money(owed - limit, settings)} over the limit`
        : `${used}% used · ${money(limit - Math.max(0, owed), settings)} left`}
    </span>
  )
}

function Dot({ kind }: { kind: StatementKind }) {
  return <span aria-hidden className={cx('inline-block size-2 shrink-0 rounded-full', DOT[kind])} />
}

/** What a line points at: its bill or credit note number, a payment's reference. */
function RowDetail({ row }: { row: StatementRow }) {
  if (row.kind === 'bill' && row.invoiceId)
    return (
      <Link
        to={`/invoices/${row.invoiceId}`}
        className="font-semibold text-primary hover:underline"
      >
        {row.number ?? 'Number on sync'}
      </Link>
    )
  if (row.kind === 'credit' && row.invoiceId)
    return (
      <Link
        to={`/invoices/${row.invoiceId}`}
        className="font-semibold text-primary hover:underline"
      >
        {row.number ?? 'Number on sync'}
      </Link>
    )
  return (
    <>
      {row.reference && <span className="text-subtle">{row.reference}</span>}
      {row.aheadPaisa > 0 && <Badge tone="warning">Paid ahead</Badge>}
    </>
  )
}

function StatementCard({
  ledger,
  range,
}: {
  ledger: CustomerLedger
  range: { from: string; to: string }
}) {
  const settings = useSettings()
  const s = buildStatement(ledger, range)
  const m = (p: number) => money(p, settings)
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-5 py-3">
        <h2 className="font-semibold">Statement</h2>
        <span className="flex gap-3 text-xs text-muted">
          {(['bill', 'payment', 'credit'] as const).map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <Dot kind={k} /> {k === 'bill' ? 'Bill' : k === 'payment' ? 'Payment' : 'Credit note'}
            </span>
          ))}
        </span>
        <span className="text-xs text-muted sm:ml-auto">
          Payments are set against the oldest bills first.
        </span>
      </div>
      <Table stack>
        <thead>
          <tr>
            <TH>Date</TH>
            <TH>What</TH>
            <TH align="right">Billed</TH>
            <TH align="right">Paid</TH>
            <TH align="right">Balance</TH>
          </tr>
        </thead>
        {/* On a phone the lines read newest first, the balance owed on top (board CustStatementPhone). */}
        <tbody className="max-sm:flex max-sm:flex-col-reverse">
          <TR className="bg-neutral-wash">
            <TD className="stack-start stack-r1 text-muted" title={dateLabel(s.from, settings)}>
              {dateShort(s.from, settings)}
            </TD>
            <TD className="stack-start stack-r2 font-semibold" colSpan={3}>
              Balance before this period
            </TD>
            <TD align="right" className="stack-end stack-r1 font-bold tabular">
              {m(s.openingPaisa)}
            </TD>
          </TR>
          {s.rows.map((r) => (
            <TR key={r.id}>
              <TD
                className="stack-start stack-r2 text-muted max-sm:text-xs"
                title={dateLabel(r.date, settings)}
              >
                {dateShort(r.date, settings)}
              </TD>
              <TD className="stack-start stack-r1">
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <Dot kind={r.kind} />
                  <span className="font-semibold">{rowWhat(r)}</span>
                  <RowDetail row={r} />
                </span>
              </TD>
              <TD
                align="right"
                className={cx('stack-end stack-r1 tabular', !r.billedPaisa && 'max-sm:hidden')}
              >
                {r.billedPaisa ? m(r.billedPaisa) : ''}
              </TD>
              <TD
                align="right"
                className={cx(
                  'stack-end stack-r1 font-semibold text-success tabular',
                  !r.paidPaisa && 'max-sm:hidden',
                )}
              >
                {r.paidPaisa ? (
                  <>
                    <span className="sm:hidden">− </span>
                    {m(r.paidPaisa)}
                  </>
                ) : (
                  ''
                )}
              </TD>
              <TD
                align="right"
                className="stack-end stack-r2 font-semibold tabular max-sm:text-xs max-sm:font-normal max-sm:text-muted"
              >
                <span className="sm:hidden">Balance </span>
                {m(r.balancePaisa)}
              </TD>
            </TR>
          ))}
          <TR className="bg-warning-light">
            <TD className="stack-start stack-r1 font-semibold text-warning max-sm:hidden">
              {dateShort(s.to, settings)}
            </TD>
            <TD className="stack-start stack-r1 font-bold text-warning">
              Owed on {dateShort(s.to, settings)}
            </TD>
            <TD align="right" className="font-bold tabular max-sm:hidden">
              {m(s.billedPaisa)}
            </TD>
            <TD align="right" className="font-bold tabular max-sm:hidden">
              {m(s.paidPaisa)}
            </TD>
            <TD
              align="right"
              className="stack-end stack-r1 text-base font-extrabold text-warning tabular"
            >
              {m(s.closingPaisa)}
            </TD>
          </TR>
        </tbody>
      </Table>
      {s.rows.length === 0 && (
        <p className="border-t border-border px-5 py-3 text-sm text-muted">
          No bills or payments in this period.
        </p>
      )}
    </Card>
  )
}

function PaymentsCard({
  ledger,
  range,
  selected,
  canUse,
  onUseAhead,
}: {
  ledger: CustomerLedger
  range: { from: string; to: string }
  selected: string | null
  canUse: boolean
  onUseAhead: () => void
}) {
  const settings = useSettings()
  const m = (p: number) => money(p, settings)
  const s = buildStatement(ledger, range)
  const rows = s.rows.filter((r) => r.kind === 'payment').reverse()
  const byId = new Map(ledger.payments.map((p) => [p.id, p]))
  const bills = new Map(ledger.bills.map((b) => [b.id, b]))
  const received = rows.reduce((sum, r) => sum + r.paidPaisa, 0)
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
        <h2 className="font-semibold">Payments</h2>
        <span className="text-sm text-muted sm:ml-auto">
          <b className="text-ink tabular">{m(received)}</b> received in this period
        </span>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No payments in this period" />
      ) : (
        <Table stack>
          <thead>
            <tr>
              <TH>Date</TH>
              <TH>How</TH>
              <TH>Set against</TH>
              <TH align="right">Amount</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = byId.get(r.id)
              return (
                <TR
                  key={r.id}
                  className={cx(selected === r.id && 'bg-primary-light')}
                  aria-current={selected === r.id ? 'true' : undefined}
                >
                  <TD
                    className="stack-start stack-r2 text-muted max-sm:text-xs"
                    title={dateLabel(r.date, settings)}
                  >
                    {dateShort(r.date, settings)}
                  </TD>
                  <TD className="stack-start stack-r1">
                    <span className="font-semibold">
                      {r.method ? methodLabel(r.method) : 'Payment'}
                    </span>
                    {r.reference && <span className="ml-2 text-sm text-subtle">{r.reference}</span>}
                  </TD>
                  <TD className="stack-r3 max-sm:mt-1.5">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {p?.allocations.map((a) => {
                        const bill = bills.get(a.invoiceId)
                        const done = bill
                          ? bill.totalPaisa - bill.paidPaisa - bill.creditedPaisa <= 0
                          : false
                        return (
                          <Chip key={a.invoiceId}>
                            <Link
                              to={`/invoices/${a.invoiceId}`}
                              className="font-semibold text-primary hover:underline"
                            >
                              {a.number ?? 'Bill'}
                            </Link>
                            <span className="tabular">{m(a.amountPaisa)}</span>
                            <span
                              className={cx('font-semibold', done ? 'text-success' : 'text-muted')}
                            >
                              {done ? 'paid off' : 'part'}
                            </span>
                          </Chip>
                        )
                      })}
                      {r.aheadPaisa > 0 && (
                        <>
                          <Badge tone="warning">Paid ahead {m(r.aheadPaisa)}</Badge>
                          {canUse ? (
                            <Button size="sm" variant="soft" onClick={onUseAhead}>
                              Set against bills
                            </Button>
                          ) : (
                            <span className="text-xs text-muted">Comes off their next bill.</span>
                          )}
                        </>
                      )}
                    </span>
                  </TD>
                  <TD align="right" className="stack-end stack-r1 font-bold text-success tabular">
                    {m(r.paidPaisa)}
                  </TD>
                </TR>
              )
            })}
          </tbody>
        </Table>
      )}
    </Card>
  )
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-border px-2.5 text-xs whitespace-nowrap">
      {children}
    </span>
  )
}

/** The customer's bills, as the page always had them; `lateOnly` from the Late number. */
function BillsCard({ customerId, lateOnly }: { customerId: string; lateOnly: boolean }) {
  const settings = useSettings()
  const navigate = useNavigate()
  const today = todayIso()
  const bills = useLive(
    async (r) => {
      const invoices = (
        await r.list('sales_invoices', {
          where: { customer_id: customerId },
          orderBy: { column: 'invoice_date', direction: 'desc' },
          limit: 200,
        })
      ).rows
      const details = await Promise.all(
        invoices.filter((i) => i.status === 'finalized').map((i) => r.invoices.detail(i.id)),
      )
      const balances = new Map(details.map((d) => [d!.invoice.id, d!.balancePaisa]))
      return { invoices, balances }
    },
    [customerId],
    [...LEDGER_TABLES],
  ).data
  const rows = bills?.invoices.filter(
    (inv) =>
      !lateOnly ||
      ((bills.balances.get(inv.id) ?? 0) > 0 && inv.due_date !== null && inv.due_date < today),
  )
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-3 border-b border-border px-5 py-3">
        <h2 className="font-semibold">{lateOnly ? 'Late bills' : 'Bills'}</h2>
        {lateOnly && (
          <Link to="?view=bills" replace className="text-sm font-semibold text-primary">
            Show every bill
          </Link>
        )}
      </div>
      {!rows ? (
        <SkeletonTable rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState title={lateOnly ? 'No late bills' : 'No invoices yet'} />
      ) : (
        <Table>
          <thead>
            <tr>
              <TH>Number</TH>
              <TH>Date</TH>
              <TH>Status</TH>
              <TH align="right">Total</TH>
              <TH align="right">Balance</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => (
              <TR
                key={inv.id}
                onClick={() => navigate(`/invoices/${inv.id}`)}
                className={inv.status === 'cancelled' ? CANCELLED_ROW : undefined}
              >
                <TD className="font-medium">
                  <Link to={`/invoices/${inv.id}`}>{inv.number ?? '—'}</Link>
                </TD>
                <TD className="text-muted" title={dateLabel(inv.invoice_date, settings)}>
                  {dateShort(inv.invoice_date, settings)}
                </TD>
                <TD>
                  <StatusBadge invoice={inv} balancePaisa={bills!.balances.get(inv.id)} />
                </TD>
                <TD align="right">{money(inv.total_paisa, settings)}</TD>
                <TD
                  align="right"
                  className={(bills!.balances.get(inv.id) ?? 0) > 0 ? 'text-warning' : 'text-muted'}
                >
                  {bills!.balances.has(inv.id)
                    ? money(bills!.balances.get(inv.id) ?? 0, settings)
                    : '—'}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  )
}
